import { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, Building2, Mail, Calendar, 
  User, AlertCircle, Loader2, FileText, MapPin, AlertTriangle,
  Download, Eye, TrendingUp, Paperclip
} from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';
import superAdminAttachmentService from '../../../../../services/audit_services/superAdminAttachmentService';

export default function AllAuditQuestionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState(null);

  useEffect(() => {
    if (id) {
      fetchQuestionDetails();
    }
  }, [id]);

  useEffect(() => {
    if (downloadMessage) {
      const timer = setTimeout(() => {
        setDownloadMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadMessage]);

  const fetchQuestionDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminAuditService.getAuditQuestionDetails(authTokens.access, id);
      setQuestionData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    setDownloadingAttachment(attachmentId);
    setDownloadMessage(null);
    try {
      const result = await superAdminAttachmentService.downloadAttachment(authTokens.access, attachmentId);
      if (result.success) {
        setDownloadMessage({ type: 'success', text: 'Fichier téléchargé avec succès' });
      } else {
        setDownloadMessage({ type: 'error', text: result.error || 'Erreur lors du téléchargement' });
      }
    } catch (err) {
      setDownloadMessage({ type: 'error', text: 'Erreur lors du téléchargement du fichier' });
    } finally {
      setDownloadingAttachment(null);
    }
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!questionData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée trouvée</p>
        </div>
      </div>
    );
  }

  const { question, organization, audit_section, created_by, last_updated_by, recommendations, initial_observations, kpis, observations, attachments, statistics } = questionData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Download Message */}
      {downloadMessage && (
        <div className={`mb-4 p-4 rounded-lg border ${
          downloadMessage.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-700' 
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          <div className="flex items-center">
            {downloadMessage.type === 'success' ? (
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            <span className="text-sm">{downloadMessage.text}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/audit-questions')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{question?.question_name}</h1>
            <p className="text-xs text-gray-500">Détails de la question d'audit</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Question Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <MessageSquare className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations de la question</h2>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Nom de la question</label>
                  <p className="text-sm text-gray-900">{question?.question_name || 'N/A'}</p>
                </div>
                {question?.location && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Localisation</label>
                    <div className="flex items-center">
                      <MapPin className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">{question.location}</p>
                    </div>
                  </div>
                )}
                {question?.status && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut</label>
                    <div>
                      <span className={`inline-block px-2 py-1 text-xs rounded ${
                        question.status === 'completed' ? 'bg-green-100 text-green-700' :
                        question.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {question.status}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                {question?.created_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Créé le</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(question.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
                {question?.updated_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Mis à jour le</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(question.updated_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {question?.question_description && (
              <div>
                <label className="text-xs font-medium text-gray-500">Description</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.question_description}</p>
              </div>
            )}

            {question?.requirements && (
              <div>
                <label className="text-xs font-medium text-gray-500">Exigences</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.requirements}</p>
              </div>
            )}

            {question?.interview_plan && (
              <div>
                <label className="text-xs font-medium text-gray-500">Plan d'entretien</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.interview_plan}</p>
              </div>
            )}

            {question?.answer && (
              <div>
                <label className="text-xs font-medium text-gray-500">Réponse</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.answer}</p>
              </div>
            )}
          </div>
        </div>

        {/* Risk Information */}
        {question?.question_risks && question.question_risks.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Risques associés ({question.question_risks.length})</h2>
            </div>
            
            <div className="space-y-4">
              {question.question_risks.map((questionRisk) => {
                const probabilityConfig = {
                  critical: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-200 text-red-900' },
                  high: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', badge: 'bg-orange-200 text-orange-900' },
                  medium: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', badge: 'bg-yellow-200 text-yellow-900' },
                  low: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-200 text-green-900' }
                };
                
                const config = probabilityConfig[questionRisk.probability] || probabilityConfig.medium;
                
                return (
                  <div key={questionRisk.id} className={`border ${config.border} rounded-lg p-4 ${config.bg}`}>
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{questionRisk.risk_name}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                        {questionRisk.probability_display}
                      </span>
                    </div>
                    
                    {questionRisk.description && (
                      <p className="text-sm text-gray-700 mb-3">{questionRisk.description}</p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Date du risque</label>
                        <p className="text-sm text-gray-900">
                          {new Date(questionRisk.risk_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Statut</label>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                          {questionRisk.status}
                        </span>
                      </div>
                    </div>
                    
                    {questionRisk.consequence && (
                      <div className="mb-3">
                        <label className="text-xs font-medium text-gray-600">Conséquence/Impact</label>
                        <p className="text-sm text-gray-900">{questionRisk.consequence}</p>
                      </div>
                    )}
                    
                    {questionRisk.original_audit_plan_risk_id && (
                      <div className="mb-3">
                        <label className="text-xs font-medium text-gray-600">Risque original du plan d'audit</label>
                        <p className="text-sm text-gray-900">#{questionRisk.original_audit_plan_risk_id}</p>
                      </div>
                    )}
                    
                    {/* Risk Actions */}
                    {questionRisk.actions && questionRisk.actions.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <label className="text-xs font-medium text-gray-600 mb-2 block">
                          Actions associées ({questionRisk.actions.length})
                        </label>
                        <div className="space-y-2">
                          {questionRisk.actions.map((action) => (
                            <div key={action.id} className="bg-white border border-gray-200 rounded p-3">
                              <p className="text-sm text-gray-900 mb-2">{action.action}</p>
                              <div className="flex items-center justify-between text-xs text-gray-600">
                                <div className="flex items-center space-x-4">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    action.is_completed 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {action.is_completed ? '✓ Complété' : '⏳ En attente'}
                                  </span>
                                  {action.due_date && (
                                    <span>Échéance: {new Date(action.due_date).toLocaleDateString('fr-FR')}</span>
                                  )}
                                </div>
                                {action.user_assigned_name && (
                                  <span>Assigné à: {action.user_assigned_name}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                      Créé le {new Date(questionRisk.created_at).toLocaleDateString('fr-FR')}
                      {questionRisk.created_by && questionRisk.created_by.name && (
                        <span> par {questionRisk.created_by.name}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Additional Question Details */}
        {(question?.declared_gaps || question?.identified_best_practices || question?.interview_result || question?.auditee_response_confirmation) && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <FileText className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Détails supplémentaires</h2>
            </div>
            
            <div className="space-y-4">
              {question.declared_gaps && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Écarts déclarés</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.declared_gaps}</p>
                </div>
              )}
              {question.identified_best_practices && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Meilleures pratiques identifiées</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.identified_best_practices}</p>
                </div>
              )}
              {question.interview_result && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Résultat de l'entretien</label>
                  <p className="text-sm text-gray-900">{question.interview_result}</p>
                </div>
              )}
              {question.auditee_response_confirmation && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Confirmation de la réponse de l'audité</label>
                  <span className={`inline-block px-2 py-1 text-xs rounded ${
                    question.auditee_response_confirmation === 'confirmed' ? 'bg-green-100 text-green-700' :
                    question.auditee_response_confirmation === 'rejected' ? 'bg-red-100 text-red-700' :
                    question.auditee_response_confirmation === 'needs_clarification' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {question.auditee_response_confirmation}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Organization */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">{organization.name}</p>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  organization.is_subscribed 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Audit Section */}
        {audit_section && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <FileText className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Section d'audit</h2>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">{audit_section.section_name}</p>
              {audit_section.location && (
                <div className="flex items-center">
                  <MapPin className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{audit_section.location}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Created By & Last Updated By */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">{created_by.full_name || 'Inconnu'}</p>
                {created_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{created_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">{last_updated_by.full_name || 'Inconnu'}</p>
                {last_updated_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Initial Observations */}
        {initial_observations && initial_observations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Eye className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Observations initiales</h2>
              </div>
              <div className="text-xs text-gray-500">
                {initial_observations.length} observation(s)
              </div>
            </div>

            <div className="space-y-3">
              {initial_observations.map((obs) => (
                <div key={obs.id} className="p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-900 mb-2">{obs.observation}</p>
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>Créé le {new Date(obs.created_at).toLocaleDateString('fr-FR')}</span>
                    {obs.created_by && (
                      <>
                        <span>•</span>
                        <span>Par {obs.created_by.name || obs.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPIs */}
        {kpis && kpis.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">KPIs</h2>
              </div>
              <div className="text-xs text-gray-500">
                {kpis.length} KPI(s)
              </div>
            </div>

            <div className="space-y-3">
              {kpis.map((kpi) => (
                <div key={kpi.id} className="p-4 rounded-lg border border-gray-200">
                  <div className="mb-2">
                    <p className="text-sm font-medium text-gray-900">{kpi.kpi_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    {kpi.kpi_value && (
                      <div>
                        <label className="text-xs font-medium text-gray-500">Valeur</label>
                        <p className="text-sm text-gray-900">{kpi.kpi_value}</p>
                      </div>
                    )}
                    {kpi.kpi_target && (
                      <div>
                        <label className="text-xs font-medium text-gray-500">Cible</label>
                        <p className="text-sm text-gray-900">{kpi.kpi_target}</p>
                      </div>
                    )}
                  </div>
                  {kpi.kpi_description && (
                    <p className="text-xs text-gray-600 mb-2">{kpi.kpi_description}</p>
                  )}
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>Créé le {new Date(kpi.created_at).toLocaleDateString('fr-FR')}</span>
                    {kpi.created_by && (
                      <>
                        <span>•</span>
                        <span>Par {kpi.created_by.name || kpi.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Observations */}
        {observations && observations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Eye className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Observations</h2>
              </div>
              <div className="text-xs text-gray-500">
                {observations.length} observation(s)
              </div>
            </div>

            <div className="space-y-3">
              {observations.map((obs) => (
                <div key={obs.id} className="p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-900 mb-2">{obs.observation}</p>
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>Créé le {new Date(obs.created_at).toLocaleDateString('fr-FR')}</span>
                    {obs.created_by && (
                      <>
                        <span>•</span>
                        <span>Par {obs.created_by.name || obs.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attachments */}
        {attachments && attachments.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Paperclip className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Pièces jointes</h2>
              </div>
              <div className="text-xs text-gray-500">
                {attachments.length} fichier(s)
              </div>
            </div>

            <div className="space-y-3">
              {attachments.map((att) => (
                <div key={att.id} className="p-4 rounded-lg border border-gray-200 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <FileText className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm font-medium text-gray-900">{att.file_name}</p>
                    </div>
                    {att.description && (
                      <p className="text-xs text-gray-600 mb-2 ml-6">{att.description}</p>
                    )}
                    <div className="flex items-center space-x-3 text-xs text-gray-500 ml-6">
                      <span>Taille: {(att.file_size / 1024).toFixed(2)} KB</span>
                      <span>•</span>
                      <span>Ajouté le {new Date(att.created_at).toLocaleDateString('fr-FR')}</span>
                      {att.uploaded_by && (
                        <>
                          <span>•</span>
                          <span>Par {att.uploaded_by.name || att.uploaded_by.email}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadAttachment(att.id, att.file_name)}
                    disabled={downloadingAttachment === att.id}
                    className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Télécharger"
                  >
                    {downloadingAttachment === att.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Recommandations</h2>
              </div>
              <div className="text-xs text-gray-500">
                {statistics?.total_recommendations || recommendations.length} recommandation(s)
              </div>
            </div>

            <div className="space-y-3">
              {recommendations.map((rec) => (
                <div key={rec.id} className="p-4 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-medium text-gray-900 flex-1">{rec.recommendation}</p>
                    <span className={`ml-2 px-2 py-1 text-xs rounded ${
                      rec.priority === 'critical' ? 'bg-red-100 text-red-700' :
                      rec.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {rec.priority}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>Créé le {new Date(rec.created_at).toLocaleDateString('fr-FR')}</span>
                    {rec.created_by && (
                      <>
                        <span>•</span>
                        <span>Par {rec.created_by.name || rec.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        {statistics && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <FileText className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Statistiques</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_recommendations}</p>
                <p className="text-xs text-gray-600">Recommandations</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_question_risks || 0}</p>
                <p className="text-xs text-gray-600">Risques</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_initial_observations || 0}</p>
                <p className="text-xs text-gray-600">Obs. initiales</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_kpis || 0}</p>
                <p className="text-xs text-gray-600">KPIs</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_observations || 0}</p>
                <p className="text-xs text-gray-600">Observations</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_attachments || 0}</p>
                <p className="text-xs text-gray-600">Pièces jointes</p>
              </div>
            </div>

            {statistics.recommendations_by_priority && (
              <>
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <p className="text-xs font-medium text-gray-500 mb-3">Recommandations par priorité</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-700">{statistics.recommendations_by_priority.critical || 0}</p>
                      <p className="text-xs text-red-700">Critique</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-700">{statistics.recommendations_by_priority.high || 0}</p>
                      <p className="text-xs text-orange-700">Élevée</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-700">{statistics.recommendations_by_priority.medium || 0}</p>
                      <p className="text-xs text-yellow-700">Moyenne</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{statistics.recommendations_by_priority.low || 0}</p>
                      <p className="text-xs text-blue-700">Faible</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {statistics.risks_by_probability && (
              <>
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <p className="text-xs font-medium text-gray-500 mb-3">Risques par probabilité</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-700">{statistics.risks_by_probability.critical || 0}</p>
                      <p className="text-xs text-red-700">Critique</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-700">{statistics.risks_by_probability.high || 0}</p>
                      <p className="text-xs text-orange-700">Élevée</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-700">{statistics.risks_by_probability.medium || 0}</p>
                      <p className="text-xs text-yellow-700">Moyenne</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{statistics.risks_by_probability.low || 0}</p>
                      <p className="text-xs text-green-700">Faible</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
